import { camelCase } from "lodash";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import { checkFiltersAreClear } from "PFApp/use_filtered_collection";
import ButtonLink from "PFComponents/button/button_link";
import { Card } from "PFComponents/card";
import DataTable from "PFComponents/data_table/data_table";
import { Icon } from "PFComponents/icon";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import sortableHeader from "PFComponents/sortable_header/sortable_header";
import Tooltip from "PFComponents/tooltip/tooltip";
import { isProfileActive } from "PFCore/helpers/profile";
import { getProfileName } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import AdminAction from "PFModules/admin/users/list/actions/admin_action";
import ChangeRequestAction from "PFModules/admin/users/list/actions/change_requests_action";
import HideAction from "PFModules/admin/users/list/actions/hide_action";
import RemoveAction from "PFModules/admin/users/list/actions/remove_action";
import SignInAsAction from "PFModules/admin/users/list/actions/sign_in_as_action";
import SuspendAction from "PFModules/admin/users/list/actions/suspend_action";
import { FeatureFlag } from "PFTypes";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import css from "./users_page.module.scss";

const UsersPage = ({
  users,
  fetchAll,
  applySort,
  order,
  pending,
  currentPage,
  total,
  totalPages,
  filters,
  handlePageChange,
  handleFilterChange,
  clearFilters
}) => {
  const { t } = useTranslation("admin", { keyPrefix: "users.list" });
  const { data: currentProfile } = useCurrentProfile();
  const isEnabled = useIsFeatureEnabled();

  const { formatDateTime } = useDateFormatter();

  const isCurrentUserSaasAdmin = currentProfile.roles.indexOf("saas_admin") >= 0;
  const [filtersShown, setFiltersShown] = useState(false);
  const filtersAreClear = checkFiltersAreClear(filters);

  const makeHeader = useCallback(
    (title, name) => () => sortableHeader(title, name, applySort, order),
    [applySort, order]
  );

  const columns = [
    {
      renderHeader: makeHeader(t("table.headers.name"), "first_name"),
      qaId: "name",
      renderCell: (user) => (
        <Link to={`/admin/users/${user.id}`} data-qa-id="UsersPage.edit_link">
          {getProfileName(user) || "..."}
        </Link>
      )
    },
    { headerName: t("table.headers.email"), cellKey: "email", qaId: "email" },
    {
      if: isCurrentUserSaasAdmin && isEnabled(FeatureFlag.SignInAdminAsSomeone),
      headerName: t("table.headers.signInAs"),
      renderCell: (user) => <SignInAsAction user={user} />,
      qaId: "sign_in_admin_as_someone"
    },
    {
      renderHeader: makeHeader(t("table.headers.lastActivity"), "last_activity_at"),
      renderCell: (user) => {
        const date = user.last_activity_at;
        return date ? formatDateTime(date) : t("never");
      },
      qaId: "last_activity_at"
    },
    {
      headerName: t("table.headers.permissionsGroup"),
      renderCell: (user) => (
        <Link
          to={`/admin/permissions_groups/${user.permissions_group.id}/edit`}
          data-qa-id="UsersPage.permissions_group"
        >
          {user.permissions_group.display_as}
        </Link>
      ),
      qaId: "permissions_group"
    },
    {
      renderHeader: makeHeader(t("table.headers.activities"), "tasks_count"),
      renderCell: (user) => user.statistics.tasks_count,
      qaId: "tasks_count"
    },
    {
      renderHeader: makeHeader(t("table.headers.profileCompletion"), "progress_completion"),
      renderCell: (user) => user.statistics.progress_completion,
      qaId: "progress_completion"
    },
    {
      renderHeader: makeHeader(t("table.headers.messagesSent"), "offered_help_count"),
      renderCell: (user) => user.statistics.offered_help_count,
      qaId: "offered_help_count"
    },
    {
      renderHeader: makeHeader(t("table.headers.skills"), "skills_count"),
      renderCell: (user) => user.statistics.skills_count,
      qaId: "skills_count"
    },
    {
      renderHeader: makeHeader(t("table.headers.rankedSkills"), "skills_ranked_count"),
      renderCell: (user) => user.statistics.skills_ranked_count,
      qaId: "skills_ranked_count"
    },
    {
      renderHeader: makeHeader(t("table.headers.pendingCVSuggestedUpdates"), "pending_change_requests_count"),
      renderCell: (user) => user.statistics.pending_change_requests_count,
      qaId: "pending_change_requests_count"
    },
    {
      if: isCurrentUserSaasAdmin,
      headerName: t("table.headers.admin"),
      renderCell: (user) => <AdminAction user={user} refetchUsers={fetchAll} />,
      qaId: "admin"
    },
    {
      renderHeader: makeHeader(t("table.headers.registrationStatus"), "registration_status"),
      renderCell: ({ registration_status }) =>
        t(`table.values.registrationStatus.${camelCase(registration_status)}`),
      qaId: "registration_status"
    },
    {
      renderHeader: makeHeader(t("table.headers.employeeStatus"), "employee_status"),
      renderCell: ({ employee_status }) => t(`table.values.status.${employee_status}`),
      qaId: "employee_status"
    },
    {
      renderHeader: makeHeader(t("table.headers.suspended"), "suspended_at"),
      renderCell: (user) => <SuspendAction user={user} refetchUsers={fetchAll} />,
      qaId: "suspended"
    },
    {
      if: isCurrentUserSaasAdmin,
      renderHeader: makeHeader(t("table.headers.visibility"), "hidden"),
      renderCell: (user) => <HideAction user={user} refetchUsers={fetchAll} />,
      qaId: "visibility"
    },
    {
      renderHeader: makeHeader(
        <Tooltip
          content={
            <>
              <div>{t("table.headers.statusTooltipContent.firstSection")}</div>
              <br />
              <div>
                <Trans
                  t={t}
                  i18nKey="table.headers.statusTooltipContent.secondSection"
                  components={[<strong key={0} />]}
                />
              </div>
              <br />
              <div>
                <Trans
                  t={t}
                  i18nKey="table.headers.statusTooltipContent.thirdSection"
                  components={[<strong key={0} />]}
                />
              </div>
            </>
          }
        >
          <span className={css.statusHeader}>
            {t("table.headers.status")} <Icon name="info" size="sm" />
          </span>
        </Tooltip>,
        "status",
        false
      ),
      renderCell: (user) => (
        <div className={isProfileActive(user) ? css.cellTrue : css.cellFalse}>
          {t(`table.values.status.${user.status}`)}
        </div>
      ),
      qaId: "status"
    },
    {
      headerName: t("table.headers.suggestedUpdates"),
      renderCell: (user) => <ChangeRequestAction user={user} />,
      qaId: "change_requests"
    },
    {
      if: isCurrentUserSaasAdmin,
      headerName: t("table.headers.remove"),
      renderCell: (user) => <RemoveAction user={user} refetchUsers={fetchAll} />,
      qaId: "remove"
    }
  ];

  return (
    <div className={css.root} style={{ opacity: pending ? 0.7 : 1 }}>
      <div className={css.header}>
        <h1>{t("manageUsers", { total })}</h1>
      </div>
      <div className={css.controls}>
        <div className={css.filtersPreviewWrap}>
          <FiltersPreview
            meta={{ order: {}, filters }}
            filtersAreClear={filtersAreClear}
            clearFilters={clearFilters}
            onFilterChange={handleFilterChange}
            onMoreClicked={() => setFiltersShown(true)}
          />
        </div>
        <div className={css.buttonsContainer}>
          <ButtonLink kind="secondary" href="/admin/users/new" qaId="Button.new_user">
            {t("toolbar.newUser")}
          </ButtonLink>
          <ButtonLink kind="secondary" href="/admin/users/send_welcome_email" qaId="Button.welcome">
            {t("toolbar.welcomeEmails")}
          </ButtonLink>
          {isCurrentUserSaasAdmin && (
            <>
              <ButtonLink kind="secondary" href="/admin/users/import" qaId="Button.import">
                {t("toolbar.import")}
              </ButtonLink>
            </>
          )}
          <FiltersButton
            style={{ paddingLeft: "5px" }}
            filtersShown={filtersShown}
            filtersAreClear={filtersAreClear}
            onClick={() => setFiltersShown(!filtersShown)}
            withLabel
          />
        </div>
      </div>
      {filtersShown && (
        <div className={css.filtersContainer}>
          <FiltersBox
            isLoading={pending}
            closeFilters={() => setFiltersShown(false)}
            meta={{ search_id: -1, filters }}
            filtersAreClear={filtersAreClear}
            clearFilters={clearFilters}
            handleFilterChange={handleFilterChange}
          />
        </div>
      )}
      {users.length === 0 ? (
        <div className={css.noUsers}>{t("noUsersFound")}</div>
      ) : (
        <>
          <Card paddingVariant="none">
            <DataTable tbodyStyle={{ whiteSpace: "nowrap" }} data={users} columns={columns} />
          </Card>
          <SimplePagination
            disabled={pending}
            currentPage={currentPage}
            totalPages={totalPages}
            handleClick={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

UsersPage.propTypes = {
  users: PropTypes.array,
  fetchAll: PropTypes.func,
  applySort: PropTypes.func,
  order: PropTypes.string,
  pending: PropTypes.bool,
  currentPage: PropTypes.number,
  total: PropTypes.number,
  totalPages: PropTypes.number,
  filters: PropTypes.array.isRequired,
  requestFilters: PropTypes.object,
  handlePageChange: PropTypes.func,
  handleFilterChange: PropTypes.func,
  clearFilters: PropTypes.func
};

export default UsersPage;
